import React from 'react';
import { Loader2, XCircle, File as FileIcon, X } from 'lucide-react';
import SmartTooltip from '../../components/SmartTooltip';
import { formatFileSize } from '../../utils/helpers';

const DOC_TYPES = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ppt: 'application/vnd.ms-powerpoint',
};

function FileThumbnail({ file, isUploading, hasError }) {
    if (isUploading) return <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />;
    if (hasError) {
        return (
            <div className="relative w-6 h-6 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
            </div>
        );
    }
    if (file.type.startsWith('image/')) {
        return <img src={file.previewUrl} alt={file.name} className="w-6 h-6 object-cover rounded" />;
    }
    if (file.type === DOC_TYPES.pdf) return <FileIcon className="w-6 h-6 text-red-500" />;
    if (file.type === DOC_TYPES.docx || file.type === DOC_TYPES.doc) {
        return <FileIcon className="w-6 h-6 text-blue-600" />;
    }
    if (file.type === DOC_TYPES.xlsx || file.type === DOC_TYPES.xls) {
        return <FileIcon className="w-6 h-6 text-green-600" />;
    }
    if (file.type === DOC_TYPES.pptx || file.type === DOC_TYPES.ppt) {
        return <FileIcon className="w-6 h-6 text-orange-600" />;
    }
    return <FileIcon className="w-6 h-6 text-gray-500" />;
}

export default function UploadedFileItem({ file, onRemove }) {
    const isUploading = file.status === 'uploading';
    const hasError = file.status === 'error';

    const fileContent = (
        <div
            className={`flex items-center justify-between bg-gray-100 rounded-lg p-2 max-w-[235px] transition-opacity ${
                isUploading || hasError ? 'opacity-50' : 'opacity-100'
            }`}
        >
            <div className="flex items-center gap-3 overflow-hidden">
                <FileThumbnail file={file} isUploading={isUploading} hasError={hasError} />
                <div className="flex-1 overflow-hidden">
                    <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                    <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                </div>
            </div>
            {onRemove && (
                <button
                    onClick={() => onRemove(file.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );

    if (hasError) {
        return (
            <SmartTooltip content={file.errorMessage || 'Lỗi upload'} width="250px" position="top">
                {fileContent}
            </SmartTooltip>
        );
    }

    return fileContent;
}
